import React, { CSSProperties, useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import Card, {
	CardActions,
	CardBody,
	CardHeader,
	CardLabel,
	CardTitle,
} from '../../components/bootstrap/Card';
import Button, { ButtonGroup } from '../../components/bootstrap/Button';
import FormGroup from '../../components/bootstrap/forms/FormGroup';
import Input from '../../components/bootstrap/forms/Input';
import Textarea from '../../components/bootstrap/forms/Textarea';
import Label from '../../components/bootstrap/forms/Label';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import Spinner from '../../components/bootstrap/Spinner';
import { SpinnerCircular } from 'spinners-react';
import { useNavigate, useParams } from 'react-router-dom';
import { JobTemplate as jobtemp } from './JobList';
import { centralApi } from '../AxiosInstance/CreateInstance';
import usejobRequisition from '../../components/utils/PermissionChecking';
import useDarkMode from '../../hooks/useDarkMode';
import Alert from '../../components/bootstrap/Alert';
import Icon from '../../components/icon/Icon';

const JobTemplate: React.FC = () => {
	const { darkModeStatus } = useDarkMode();
	const navigate = useNavigate();
	let { updateId } = useParams();
	const [template, setTemplate] = useState('');
	const [loading, setLoading] = useState(false);
	const [isManualFallback, setIsManualFallback] = useState(!!updateId);
	const [getTemplte, setGetTemplate] = useState<jobtemp>();
	const [jdTemplate, setJdTemplate] = useState<jobtemp[]>([]);


	const { hasPermission } = usejobRequisition();
	const moduleName = 'Job Template List';
	const canAdd = hasPermission(moduleName, 'attach interview');
	const canUpdate = hasPermission(moduleName, 'attach interview');
	const canDelete = hasPermission(moduleName, 'attach interview');
	const canView = hasPermission(moduleName, 'attach interview');
	const canViewJD = hasPermission(moduleName, 'attach interview');
	const canDownloadJD = hasPermission(moduleName, 'view applied candidate');

	useEffect(() => {
		const fetchAllJdTemplates = async () => {
			let allTemplates: any[] = [];
			let pageNo = 1;
			const limit = 30;
			let hasMore = true;

			try {
				while (hasMore) {
					// eslint-disable-next-line no-await-in-loop
					const res = await centralApi.get(
						`jobService/jd-template/list?pageNo=${pageNo}&limit=${limit}`,
					);

					const data = res.data?.data || [];
					allTemplates = [...allTemplates, ...data];

					if (data.length < limit) {
						hasMore = false;
					} else {
						pageNo += 1;
					}
				}

				setJdTemplate(allTemplates);
			} catch (err) {
				// toast.error('Error while fetching templates', { closeOnClick: true });
			}
		};

		fetchAllJdTemplates();
	}, []);

	useEffect(() => {
		if (updateId && jdTemplate.length > 0) {
			const found = jdTemplate.find((item) => item._id === updateId);
			setGetTemplate(found);
			if (
				found &&
				found.description &&
				Array.isArray(found.description) &&
				found.description.length > 0
			) {
				const latestDescription = found.description.reduce((latest, current) =>
					current.version > latest.version ? current : latest,
				);
				setTemplate(latestDescription.content || '');
				const hasSkillsOrExp =
					(latestDescription.requiredSkills && latestDescription.requiredSkills.trim()) ||
					(latestDescription.experience && latestDescription.experience.trim());
				setIsManualFallback(!hasSkillsOrExp);
			}
		}
	}, [updateId, jdTemplate]);

	const latestDescription =
		getTemplte?.description && getTemplte.description.length > 0
			? getTemplte.description.reduce(
				(latest, current) => (current.version > latest.version ? current : latest),
				getTemplte.description[0],
			)
			: undefined;

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			title: getTemplte?.title || '',
			skills: latestDescription?.requiredSkills || '',
			description: latestDescription?.experience || '',
		},
		validate: (values) => {
			const errors: {
				title?: string;
				skills?: string;
				description?: string;
			} = {};

			if (!values.title || values.title.trim().length < 5) {
				errors.title = 'Job title must be at least 5 characters.';
			} else if (
				jdTemplate.find(
					(item) =>
						item.title.toLowerCase().trim() === values.title.toLowerCase().trim() &&
						item._id !== updateId,
				)
			) {
				errors.title = 'Job title already exists.';
			} else if (values.title.length > 100) {
				errors.title = 'Job title cannot exceed 100 characters.';
			} else if (!/^[A-Za-z0-9 ()\.#\/&-]+$/.test(values.title.trim())) {
				errors.title = 'Job title contains invalid characters.';
			}

			if (!isManualFallback) {
				if (!values.skills || !values.skills.trim()) {
					errors.skills = 'Skills are required.';
				} else if (values.skills.length < 3) {
					errors.skills = 'Skills must be at least 3 characters.';
				} else if (values.skills.length > 200) {
					errors.skills = 'Skills cannot exceed 200 characters.';
				} else {
					const skillList = values.skills.split(',').map((s) => s.trim());
					if (skillList.some((skill) => !/^[a-zA-Z0-9.+\-\/# ]+$/.test(skill))) {
						errors.skills = 'Each skill must be alphanumeric (e.g., React, Node.js, CI/CD).';
					}

				}

				if (!values.description || !values.description.trim()) {
					errors.description = 'Experience is required.';
				} else if (
					!/^(fresher|\d+(\.\d+)?(\s*(month|months|year|years|yr|yrs))?(\s*-\s*\d+(\.\d+)?\s*(month|months|year|years|yr|yrs))?)$/i
						.test(values.description.trim().replace(/\s+/g, ' '))
				) {
					errors.description =
						"Experience must be like 'Fresher', '1.5 years', '6 months - 1 year', or '0 - 6 months'.";
				}

			}
			return errors;
		},
		onSubmit: async (values) => {
			if (Object.keys(formik.errors).length > 0) {
				toast.error('Please fill required fields.', {
					closeOnClick: true,
				});
				return;
			}

			setLoading(true);
			try {

				const response = await centralApi.post('jobService/jd-template/generateTemplate', {
					title: values.title,
					requiredSkills: values.skills,
					experience: values.description,
				});

				const responseData = response.data;
				if (
					responseData &&
					(responseData.status === 429 ||
						(responseData.status === 400 && responseData.msg?.toLowerCase().includes('credit')))
				) {
					toast.error(
						'AI Job Description generation is currently unavailable. You can enter the Job Description manually.',
						{
							closeOnClick: true,
						},
					);

					setIsManualFallback(true);
					setTemplate('');
				} else if (responseData && responseData.status === 200 && responseData.data) {
					const { content } = responseData.data;
					const cleanedHTML = content
						.replace(/<li>\s*<\/li>/g, '')
						.replace(/\n/g, '')
						.replace(/\s{2,}/g, ' ');

					setTemplate(cleanedHTML);
					setIsManualFallback(false);
					toast.success('Content Generated Successfully', {
						closeOnClick: true,
					});
				} else {
					toast.error(responseData?.msg || 'Failed to generate content', {
						closeOnClick: true,
					});
				}
			} catch (error: any) {
				const errorMsg =
					error?.response?.data?.msg ||
					error?.response?.data?.message ||
					'';

				const status =
					error?.response?.status ||
					error?.response?.data?.status;

				const isCreditError =
					status === 429 ||
					errorMsg.toLowerCase().includes('credit') ||
					errorMsg.toLowerCase().includes('billing');

				if (isCreditError) {
					toast.error(
						'AI Job Description generation is temporarily unavailable. Please enter the Job Description manually to continue.',
						{
							closeOnClick: true,
						},
					);

					setIsManualFallback(true);
					setTemplate('');
				} else {
					toast.error(
						'Failed to generate Job Description. Please try again.',
						{
							closeOnClick: true,
						},
					);
				}
			} finally {
				setLoading(false);
			}
		},
	});

	const handleSaveTemplate = async () => {
		const errors = await formik.validateForm();
		const payload = isManualFallback
			? {
				title: formik.values.title,
				content: template,
			}
			: {
				title: formik.values.title,
				content: template,
				requiredSkills: formik.values.skills,
				experience: formik.values.description,
			};
		if (Object.keys(errors).length > 0) {
			formik.setTouched({
				title: true,
				...(isManualFallback
					? {}
					: {
						skills: true,
						description: true,
					}),
			});
			toast.error('Please fill required fields.', {
				closeOnClick: true,
			});
			return;
		}
		if (!template || !template.trim() || template === '<p><br></p>') {
			toast.error('Job description content cannot be empty.', {
				closeOnClick: true,
			});
			return;
		}
		if (updateId) {
			try {
				await centralApi
					.patch('jobService/jd-template/update', {
						templateId: updateId,
						...payload,
					})
					.then((res) => {
						toast.success('Template updated successfully', {
							closeOnClick: true,
						});
						updateId = '';
						navigate('/job-list');
						formik.resetForm();
					});
			} catch (error) {
				toast.error('Error while updating Template', {
					closeOnClick: true,
				});
			}
		} else {
			try {
				await centralApi
					.post('jobService/jd-template/add', payload)
					.then((res) => {
						toast.success('JD template saved successfully!', {
							closeOnClick: true,
						});
						navigate('/job-list');
						formik.resetForm();
					});
			} catch (err) {
				toast.error('Error saving JD template.', {
					closeOnClick: true,
				});
			}
		}
	};

	const handleCancel = () => {
		navigate('/job-list');
	};

	const buttonStyles: CSSProperties = {
		background:
			darkModeStatus
				? 'linear-gradient(45deg, #45b7d1, #4ecdc4)'
				: 'linear-gradient(45deg, #4ecdc4, #45b7d1)',
		border: 'none',
		color: '#fff',
		transition: 'transform 0.2s ease, box-shadow 0.2s ease, opacity 0.3s ease',
		boxShadow: '0 4px 12px rgba(78,205,196,0.3)',
	};

	const buttonHoverStyles = {
		transform: 'scale(1.01)',
		boxShadow: '0 6px 16px rgba(78,205,196,0.5)',
		opacity: 0.9,
	};

	const buttonLeaveStyles = {
		transform: 'scale(1)',
		boxShadow: '0 4px 12px rgba(78,205,196,0.3)',
		opacity: 1,
	};

	function capitalizeWords(str: string) {
		if (!str) return '';
		return str
			?.toLowerCase()
			.split(' ')
			.map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	}

	let aiGenerateButtonColor: 'info' | 'dark' | 'light' = 'light';
	if (!isManualFallback) {
		aiGenerateButtonColor = 'info';
	} else if (darkModeStatus) {
		aiGenerateButtonColor = 'dark';
	}

	let writeManuallyButtonColor: 'info' | 'dark' | 'light' = 'light';
	if (isManualFallback) {
		writeManuallyButtonColor = 'info';
	} else if (darkModeStatus) {
		writeManuallyButtonColor = 'dark';
	}

	return (
		<PageWrapper title='Job Description Template' className='mt-4'>
			<Card stretch='full' style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
				<CardHeader borderSize={1}>
					<CardLabel icon='cardTravel' iconColor='info' className='ms-2'>
						<CardTitle>Job Description Template</CardTitle>
					</CardLabel>
					<CardActions>
						<Button color='dark' onClick={() => handleCancel()}>
							Cancel
						</Button>
					</CardActions>
				</CardHeader>

				<CardBody style={{ flex: 1, overflowY: 'auto' }}>
					<div className='row g-3' style={{ height: '100%' }}>
						<div className='col-md-5 col-12'>
							<Card className='d-flex flex-column h-100'>
								<CardHeader
									borderSize={1}
									className='d-flex justify-content-center'>
									<CardLabel icon='' iconColor='info' className='text-center'>
										<CardTitle>Job Description Template</CardTitle>
									</CardLabel>
								</CardHeader>
								<CardBody className='d-flex flex-column' isScrollable>
									<form
										onSubmit={formik.handleSubmit}
										style={{
											flexGrow: 1,
											display: 'flex',
											flexDirection: 'column',
										}}>
										<div className='mb-4 text-center'>
											<ButtonGroup className='w-100'>
												<Button
													type='button'
													color={aiGenerateButtonColor}
													isLight={isManualFallback}
													className='w-50'
													onClick={() => {
														setIsManualFallback(false);
														formik.setErrors({});
														formik.setTouched({});
													}}>
													AI Generate
												</Button>
												<Button
													type='button'
													color={writeManuallyButtonColor}
													isLight={!isManualFallback}
													className='w-50'
													onClick={() => {
														setIsManualFallback(true);
														formik.setErrors({});
														formik.setTouched({});
													}}>
													Write Manually
												</Button>
											</ButtonGroup>
										</div>
										<div className='mb-4'>
											<FormGroup id='job-title' className='mb-4'>
												<Label htmlFor='job-title'>Job Title <span className='text-danger'>*</span></Label>
												<Input
													name='title'
													placeholder='e.g. Frontend Developer'
													id='job-title'
													type='text'
													className='form-control py-2'
													value={formik.values.title}
													onBlur={formik.handleBlur}
													onChange={formik.handleChange}
													isTouched={!!formik.touched.title}
													invalidFeedback={formik.errors.title}
													isValid={formik.isValid && !formik.errors.title}
												/>
											</FormGroup>
										</div>
										{!isManualFallback && (
											<>
												<div className='mb-4'>
													<FormGroup id='req-skills' className='mb-4'>
														<Label htmlFor='req-skills'>Required Skills <span className='text-danger'>*</span></Label>
														<Textarea
															placeholder='e.g. React, TypeScript, Node.js'
															id='req-skills'
															name='skills'
															className='form-control py-2'
															value={capitalizeWords(formik.values.skills)}
															onChange={formik.handleChange}
															isTouched={!!formik.touched.skills}
															isValid={
																formik.isValid && !formik.errors.skills
															}
															onBlur={formik.handleBlur}
															invalidFeedback={formik.errors.skills}
														/>
													</FormGroup>
												</div>
												<div className='mb-3'>
													<FormGroup id='exp-level' className='mb-4'>
														<Label htmlFor='exp-level'>Experience Level <span className='text-danger'>*</span></Label>
														<Input
															placeholder='e.g. 3-5 years, Fresher, 1.5 years'
															id='exp-level'
															name='description'
															type='text'
															className='form-control py-2'
															value={formik.values.description}
															onChange={formik.handleChange}
															isTouched={!!formik.touched.description}
															onBlur={formik.handleBlur}
															isValid={
																formik.isValid && !formik.errors.description
															}
															invalidFeedback={formik.errors.description}
														/>
													</FormGroup>
												</div>
											</>
										)}
										{!isManualFallback && (
											<div style={{ marginTop: 'auto', paddingTop: '1rem' }}>
												<Button
													type='submit'
													className='w-100 p-3 hrms-gradient-btn'
													style={buttonStyles}
													isDisable={loading}>
													{loading && (
														<>
															<Spinner isSmall inButton isGrow />
															<Spinner isSmall inButton isGrow />
															<Spinner isSmall inButton isGrow />
														</>
													)}
													{loading
														? 'Generating...'
														: 'Generate Job Description'}
												</Button>
											</div>
										)}
									</form>
								</CardBody>
							</Card>
						</div>

						<div className='col-md-7 col-12'>
							<Card className='d-flex flex-column h-100'>
								<CardHeader
									borderSize={1}
									className='d-flex justify-content-center'>
									<CardLabel>
										<CardTitle>{isManualFallback ? 'Manual Job Description' : 'Generated Templates'}</CardTitle>
									</CardLabel>
								</CardHeader>
								<CardBody className='d-flex flex-column' isScrollable>
									{template.length === 0 && !loading && !updateId && !isManualFallback && (
										<div
											className='text-muted d-flex justify-content-center align-items-center'
											style={{ flexGrow: 1 }}>
											No templates yet.
										</div>
									)}
									{loading && (
										<div
											className='text-muted d-flex flex-column justify-content-center align-items-center'
											style={{ flexGrow: 1 }}>
											<SpinnerCircular
												enabled={loading}
												color='#45b7d1'
												thickness={100}
												size={50}
												secondaryColor=''
											/>
											<p className='mt-2'>Loading...</p>
										</div>
									)}
									{(template || updateId || isManualFallback) && !loading && (
										<>
											{isManualFallback && (
												<Alert
													color='info'
													isLight
													icon='Edit'
													className='mb-3'>
													<strong>Manual Entry Mode:</strong> Write or paste the job description below.
												</Alert>
											)}
											<style>{`
												@media (min-width: 992px) {
													.job-description-editor .ql-container {
														min-height: calc(100vh - 460px);
													}
												}
												@media (max-width: 991.98px) {
													.job-description-editor .ql-container {
														min-height: 250px;
													}
												}
												.job-description-editor .ql-editor,
												.job-description-editor .ql-editor * {
													color: ${darkModeStatus ? '#f8f9fa' : '#212529'} !important;
												}
												.form-label {
													color: ${darkModeStatus ? '#f8f9fa' : '#212529'} !important;
												}
											`}</style>
											<Label htmlFor='text-editor'>Text Editor</Label>
											<div>
												<ReactQuill
													value={template}
													onChange={setTemplate}
													placeholder='Edit job description here...'
													theme='snow'
													className='job-description-editor'
												/>
											</div>

											<div style={{ marginTop: 'auto', paddingTop: '1rem' }}>
												<Button
													className='w-100 p-3 btn-success hrms-gradient-btn'
													onClick={handleSaveTemplate}
													style={buttonStyles}
												>
													{updateId
														? 'Update Template'
														: 'Save as Template'}
												</Button>
											</div>
										</>
									)}
								</CardBody>
							</Card>
						</div>
					</div>
				</CardBody>
			</Card>
		</PageWrapper>
	);
};

export default JobTemplate;
